import { Component, Input, Output, EventEmitter, OnInit, computed, ApplicationRef, ComponentRef, createComponent, EnvironmentInjector, OnDestroy, inject, effect, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRouteSnapshot, RouterModule, Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { SidebarMenuItem } from '../../../core/models/taskboard.models';
import { environment } from '../../../../environments/environment';
import { StorageService } from '../../../core/services/storage.service';
import { STORAGE } from '../../constants/storage.constants';
import { SidebarTooltipComponent } from './sidebar-tooltip.component';
import { ComingSoonFeatureModalComponent } from '../coming-soon-feature-modal/coming-soon-feature-modal.component';
import { PermissionService } from '../../../core/services/permission.service';
import { NavigationGuardService } from '../../../core/services/navigation-guard.service';
import { ActionFeedbackService } from '../../../core/services/action-feedback.service';
import { SYSTEM_PERMISSION_LIST } from '../../constants/permissions.constants';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  isProjectDetailFromContractDevelopment,
  isProjectDetailFromProduction,
  isProjectDetailFromWarrantyProject,
  isProjectDevelopmentModuleSubpath
} from '../../utils/route.util';
import { buildWipHandoffUrl } from '../../utils/wip-handoff.util';
import { NotificationsPollService } from '../../../core/services/notifications-poll.service';
import { AioNavigationComponent, AioFooterComponent, type AioNavItem, type AioNavPress, type AioFooterLink } from '@aio/ui';
@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [CommonModule, RouterModule, ComingSoonFeatureModalComponent, AioNavigationComponent, AioFooterComponent],
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  host: {
    '[class.collapsed]': 'sidebarCollapsed',
  },
})
export class SidebarComponent implements OnInit, OnDestroy {
  /** Sidebar footer link; configured in `environment*.ts` as `helpSupportCenterUrl`. */
  readonly helpSupportCenterUrl = environment.helpSupportCenterUrl ?? '';

  get helpFooterLinks(): AioFooterLink[] {
    return [
      {
        id: 'help',
        label: 'Help & Support Center',
        href: this.helpSupportCenterUrl,
        target: '_blank',
        rel: 'noopener noreferrer',
        iconSrc: 'assets/icons/help.svg',
      },
    ];
  }

  @Input() set sidebarCollapsed(value: boolean) {
    this._sidebarCollapsed = value;
    // When sidebar is collapsed, close all non-active tabs
    if (value) {
      this.closeNonActiveTabs();
    }
  }
  get sidebarCollapsed(): boolean {
    return this._sidebarCollapsed;
  }
  private _sidebarCollapsed = false;

  @Output() expandSidebar = new EventEmitter<void>();

  hoveredItem: SidebarMenuItem | null = null;
  tooltipPosition = { left: 0, top: 0 };
  private hoveringTooltip = false;
  private tooltipHideTimeout: any = null;
  private tooltipComponentRef: ComponentRef<any> | null = null;

  private readonly permissionService = inject(PermissionService);
  private readonly router = inject(Router);
  private readonly navigationGuardService = inject(NavigationGuardService);
  private readonly actionFeedback = inject(ActionFeedbackService);
  readonly notificationsPoll = inject(NotificationsPollService);

  constructor(
    private route: ActivatedRoute,
    private storageService: StorageService,
    private appRef: ApplicationRef,
    private injector: EnvironmentInjector
  ) {
    // Watch for admin mode changes
    effect(() => {
      this.isAdminMode(); // This will trigger the effect when admin mode changes
      this.updateMenuRoutes();
    });
  }

  /** Ticks on every NavigationEnd so tenantId computed re-runs and picks up the new route. */
  private readonly routeChanged = signal(0);

  readonly tenantId = computed(() => {
    this.routeChanged(); // Re-run when route changes
    let route: ActivatedRouteSnapshot | null = this.router.routerState.snapshot.root;
    while (route) {
      const id = route.paramMap.get('tenantId');
      if (id) return id;
      route = route.firstChild ?? null;
    }
    return '';
  });

  readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      // map to the latest URL
      map(() => this.router.url) ),
      { initialValue: this.router.url }
  );

  readonly isAdminMode = computed(() => {
    const url = this.currentUrl();
    return url.includes('/admin');
  });

  menu: SidebarMenuItem[] = [];
  private readonly navItemIndex = new Map<string, SidebarMenuItem>();

  get libraryNavItems(): AioNavItem[] {
    this.navItemIndex.clear();
    return this.menu.map((item) => this.toNavItem(item, item.label, 0));
  }

  private toNavItem(item: SidebarMenuItem, id: string, depth: number): AioNavItem {
    this.navItemIndex.set(id, item);
    const hasChildren = !!item.children?.length;
    const current = !hasChildren && (depth === 0 ? this.isItemActive(item) : this.isSubItemActive(item));
    const activeParent = hasChildren && this.hasActiveChild(item);
    let badge: string | undefined;
    if (this.showComingSoonChip(item)) {
      badge = 'Coming Soon';
    } else if (this.showCommunicationsUnreadBadge(item)) {
      badge = this.communicationsUnreadBadgeLabel();
    }
    return {
      id,
      label: item.label,
      iconSrc: depth === 0
        ? (current || activeParent ? item.iconActive || item.icon : item.icon)
        : undefined,
      open: activeParent,
      expanded: hasChildren ? (depth === 0 ? this.shouldBeExpanded(item) : true) : false,
      disabled: !!item.disabled && !this.showComingSoonChip(item),
      current,
      badge,
      href: this.hrefForSidebarItem(item) ?? undefined,
      target: this.anchorTarget(item) ?? undefined,
      rel: this.anchorRel(item) ?? undefined,
      external: this.showsExternalLaunchIcon(item),
      title: item.disabled
        ? (item.disabledTooltip ?? 'Feature to be implemented in a future update')
        : undefined,
      children: item.children?.map((child) => this.toNavItem(child, `${id}/${child.label}`, depth + 1)),
    };
  }

  onNavPress(press: AioNavPress): void {
    const original = this.navItemIndex.get(press.item.id);
    if (!original) {
      return;
    }
    if (original.children?.length && !press.item.id.includes('/')) {
      this.toggleExpand(press.event, original);
      return;
    }
    if (original.children?.length) {
      this.onSubmenuParentClick(press.event, original);
      return;
    }
    this.onDirectNavigationClick(press.event as MouseEvent, original);
  }

  onNavHover(hover: { item: AioNavItem; event: MouseEvent }): void {
    if (!this.sidebarCollapsed) {
      return;
    }
    const original = this.navItemIndex.get(hover.item.id);
    if (!original?.children?.length || hover.item.id.includes('/')) {
      return;
    }
    this.onParentHover(hover.event, original);
  }

  comingSoonModalOpen = false;
  comingSoonFeatureLabel = '';

  // Permission checking methods
  private hasLeadsPermission(): boolean {
    return this.permissionService.hasAnyPermissionSync([SYSTEM_PERMISSION_LIST.LEADS_VIEW]);
  }

  private hasOpportunitiesPermission(): boolean {
    return this.permissionService.hasAnyPermissionSync([SYSTEM_PERMISSION_LIST.OPPORTUNITIES_VIEW]);
  }

  private hasAdminPermission(): boolean {
    return this.permissionService.hasAnyPermissionSync([
      SYSTEM_PERMISSION_LIST.USERS_VIEW_ALL,
      SYSTEM_PERMISSION_LIST.ROLES_VIEW
    ]);
  }

  private hasUsersPermission(): boolean {
    return this.permissionService.hasAnyPermissionSync([SYSTEM_PERMISSION_LIST.USERS_VIEW_ALL]);
  }

  private hasRolesPermission(): boolean {
    return this.permissionService.hasAnyPermissionSync([SYSTEM_PERMISSION_LIST.ROLES_VIEW]);
  }

  private hasContactsPermission(): boolean {
    // Assuming contacts permission - you may need to add this to permissions.constants.ts
    return this.permissionService.hasAnyPermissionSync([SYSTEM_PERMISSION_LIST.CONTACTS_VIEW]);
  }

  private hasWorkboardsPermission(): boolean {
    return this.permissionService.hasAnyPermissionSync([SYSTEM_PERMISSION_LIST.BOARDS_VIEW]);
  }

  /** Employee Labor Rates reads wage data — same gate the compensation API applies. */
  private hasLaborCostViewPermission(): boolean {
    return this.permissionService.hasAnyPermissionSync([SYSTEM_PERMISSION_LIST.LABOR_COST_VIEW]);
  }

  /** Global Labor Settings loads the LABOR_SETTING singleton, whose GET is gated on the admin time-entry view. */
  private hasLaborSettingsViewPermission(): boolean {
    return this.permissionService.hasAnyPermissionSync([SYSTEM_PERMISSION_LIST.TIME_ENTRY_ADMIN_VIEW]);
  }

  /** QBO Account (Finance Settings): every accounting-sync API route answers to Manage, Operate, View or Approve. */
  private hasAccountingSyncPermission(): boolean {
    return this.permissionService.hasAnyPermissionSync([
      SYSTEM_PERMISSION_LIST.ACCOUNTING_SYNC_MANAGE,
      SYSTEM_PERMISSION_LIST.ACCOUNTING_SYNC_OPERATE,
      SYSTEM_PERMISSION_LIST.ACCOUNTING_SYNC_VIEW,
      SYSTEM_PERMISSION_LIST.ACCOUNTING_SYNC_APPROVE,
    ]);
  }

  /** Directory or directory-manage permission — show Directory nav link. */
  private hasDirectoryViewPermission(): boolean {
    return this.permissionService.hasAnyPermissionSync([
      SYSTEM_PERMISSION_LIST.DIRECTORY_VIEW,
      SYSTEM_PERMISSION_LIST.DIRECTORY_ADD_EDIT_DELETE,
    ]);
  }

  /** Org chart view or manage — show Org chart nav link. */
  private hasOrgChartViewPermission(): boolean {
    return this.permissionService.hasAnyPermissionSync([
      SYSTEM_PERMISSION_LIST.ORG_CHART_VIEW,
      SYSTEM_PERMISSION_LIST.ORG_CHART_ADD_EDIT_DELETE,
    ]);
  }

  private hasJobCostingPermission(): boolean {
    return this.permissionService.hasAnyPermissionSync([SYSTEM_PERMISSION_LIST.JOB_COST_VIEW]);
  }

  private teamSidebarChildren(basePath: string): SidebarMenuItem[] {
    const children: SidebarMenuItem[] = [];
    if (this.hasDirectoryViewPermission()) {
      children.push({
        label: 'Directory',
        route: `${basePath}/team/directory`,
      });
    }
    if (this.hasOrgChartViewPermission()) {
      children.push({
        label: 'Org chart',
        route: `${basePath}/team/org-chart`,
      });
    }
    children.push({
      label: 'Files',
      route: `${basePath}/team/files`,
      disabled: !this.hasDirectoryViewPermission() && !this.hasOrgChartViewPermission(),
    });
    return children;
  }

  /** Admin sidebar: company area (branding, files, directory, org chart). */
  private adminCompanySettingsChildren(basePath: string): SidebarMenuItem[] {
    const children: SidebarMenuItem[] = [];
    if (this.hasDirectoryViewPermission()) {
      children.push({ label: 'Directory', route: `${basePath}/admin/team/directory` });
    }
    if (this.hasOrgChartViewPermission()) {
      children.push({ label: 'Org Chart', route: `${basePath}/admin/team/org-chart` });
    }
    children.push(
      { label: 'Branding & Details', route: `${basePath}/admin/company-settings/branding` },
      {
        label: 'Files',
        route: `${basePath}/admin/files`,
        disabled: !this.hasDirectoryViewPermission() && !this.hasOrgChartViewPermission(),
      },
    );
    return children;
  }

  private routeEventsSub?: ReturnType<Router['events']['subscribe']>;
  private permissionsChangedSub?: Subscription;

  ngOnInit() {
    this.updateMenuRoutes();

    // Re-run tenantId computed and update menu when the route changes
    this.routeEventsSub = this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(() => {
        this.routeChanged.update((v) => v + 1);
        this.updateMenuRoutes();
      });

    this.permissionsChangedSub = this.permissionService.permissionsChanged$.subscribe(() => {
      this.updateMenuRoutes();
    });

    // Check for scrollable content after a short delay to ensure DOM is ready
    setTimeout(() => {
      this.checkScrollableContent();
    }, 100);
  }

  // Check if sidebar content is scrollable
  private checkScrollableContent(): void {
    const sidebarNav = document.querySelector('.sidebar-nav') as HTMLElement;
    if (sidebarNav) {
      const hasScrollableContent = sidebarNav.scrollHeight > sidebarNav.clientHeight;
      const sidebar = document.querySelector('.sidebar') as HTMLElement;
      if (sidebar) {
        if (hasScrollableContent) {
          sidebar.classList.add('has-scrollable-content');
        } else {
          sidebar.classList.remove('has-scrollable-content');
        }
      }
    }
  }

  /** True if this menu node or any descendant has a route matching the current URL. */
  /** True when the daily home hub is open — no sidebar item should look selected. */
  private isDailyHomeRoute(): boolean {
    const path = this.router.url.split('?')[0];
    return /^\/[^/]+\/home\/?$/.test(path);
  }

  private menuBranchIsActive(node: SidebarMenuItem): boolean {
    if (this.isDailyHomeRoute()) return false;
    const currentUrl = this.router.url;
    if (node.children?.length) {
      return node.children.some(c => this.menuBranchIsActive(c));
    }
    if (node.disabled) return false;
    if (!node.route) return false;
    return this.isSubItemActive(node);
  }

  // Check if a parent menu item has an active child
  hasActiveChild(item: SidebarMenuItem): boolean {
    if (!item.children) return false;
    return item.children.some(child => this.menuBranchIsActive(child));
  }

  // Check if a parent menu item should be expanded (has active child or was manually expanded)
  shouldBeExpanded(item: SidebarMenuItem): boolean {
    if (!item.children) return false;
    if (item.expanded) return true;
    return this.hasActiveChild(item);
  }

  // Close all non-active tabs when sidebar is collapsed
  private closeNonActiveTabs(): void {
    this.menu.forEach(item => {
      if (item.children && !this.hasActiveChild(item)) {
        item.expanded = false;
      }
    });
    this.saveExpandedStates();
  }

  /** Show chip for disabled placeholder items only (excludes permission / disabledTooltip gating). */
  showComingSoonChip(item: SidebarMenuItem): boolean {
    return !!(item.disabled && item.comingSoon);
  }

  protected showCommunicationsUnreadBadge(item: SidebarMenuItem): boolean {
    return item.label === 'Communications' && this.notificationsPoll.effectiveCommunicationsThreadUnreadCount() > 0;
  }

  protected communicationsUnreadBadgeLabel(): string {
    return this.formatNavBadgeCount(this.notificationsPoll.effectiveCommunicationsThreadUnreadCount());
  }

  private formatNavBadgeCount(count: number): string {
    if (count > 99) {
      return '99+';
    }
    return String(count);
  }

  openComingSoonModal(featureLabel: string): void {
    this.comingSoonFeatureLabel = featureLabel;
    this.comingSoonModalOpen = true;
  }

  onComingSoonModalClose(): void {
    this.comingSoonModalOpen = false;
    this.comingSoonFeatureLabel = '';
  }

  onComingSoonNotifyMe(): void {
    this.comingSoonModalOpen = false;
    this.comingSoonFeatureLabel = '';
    this.actionFeedback.showSuccess("You'll be notified via email when this feature is available.", {
      autoHideMs: 5000,
    });
  }

  // Check if a main item is currently active
  isItemActive(item: SidebarMenuItem): boolean {
    if (this.isDailyHomeRoute()) return false;
    if (item.children) return false;
    const currentUrl = this.router.url;
    const basePath = this.tenantId() ? `/${this.tenantId()}` : '';

    if (!item.route) return false;
    const urlPath = currentUrl.split('?')[0];
    const routePath = item.route.split('?')[0];

    // Exact match (handles /tenantId/calendar)
    if (urlPath === routePath) {
      return true;
    }

    // Match if URL starts with route followed by query params (handles /tenantId/calendar?view=month)
    // But don't match if there are additional path segments (e.g., /tenantId/calendar/something)
    if (currentUrl.startsWith(routePath + '?') || currentUrl.startsWith(routePath + '#')) {
      return true;
    }

    // Warranty list + project detail share /:tenant/warranty/projects; detail is /warranty/projects/view/:id
    if (routePath.endsWith('/warranty/projects') && urlPath.startsWith(routePath + '/')) {
      return true;
    }

    return false;
  }

  // Check if a sub-item is currently active
  isSubItemActive(subItem: any): boolean {
    if (this.isDailyHomeRoute()) return false;
    if (subItem.children?.length) {
      return subItem.children.some((n: SidebarMenuItem) => this.isSubItemActive(n));
    }
    if (subItem.disabled) return false;
    if (!subItem.route) return false;
    const currentUrl = this.router.url;

    // Special handling for Users in admin mode
    if (subItem.label === 'Users' && this.isAdminMode()) {
      const pathOnly = currentUrl.split('?')[0];
      if (pathOnly.includes('/admin/users')) {
        return true;
      }
      // Any /admin/… path contains "/admin"; only treat the legacy bare /:tenantId/admin route as Users
      return /\/admin\/?$/.test(pathOnly) && !currentUrl.includes('?tab=');
    }

    // Special handling for Roles in admin mode
    if (subItem.label === 'Roles' && this.isAdminMode()) {
      // Active if on /admin/roles or /admin with roles tab
      return currentUrl.includes('/admin/roles') || currentUrl.includes('/admin?tab=roles');
    }

    const path = currentUrl.split('?')[0];
    const route = subItem.route as string;

    // Project Development: /projects (cards) vs /projects/contract-development (list) share a path prefix
    if (route.endsWith('/projects/contract-development')) {
      if (path.includes('/projects/contract-development')) return true;
      if (isProjectDevelopmentModuleSubpath(path) && isProjectDetailFromContractDevelopment(currentUrl)) {
        return true;
      }
      return false;
    }
    if (route.endsWith('/projects') && !route.includes('contract-development')) {
      if (!path.startsWith(route)) return false;
      if (path.includes('/projects/contract-development')) return false;
      if (isProjectDevelopmentModuleSubpath(path) && isProjectDetailFromContractDevelopment(currentUrl)) {
        return false;
      }
      if (isProjectDevelopmentModuleSubpath(path) && isProjectDetailFromProduction(currentUrl)) {
        return false;
      }
      if (isProjectDevelopmentModuleSubpath(path) && isProjectDetailFromWarrantyProject(currentUrl)) {
        return false;
      }
      return path === route || path.startsWith(`${route}/`);
    }

    // Production: list/detail plus estimating/specs/selections opened with ?from=production
    if (route.endsWith('/production-projects')) {
      if (path.includes('/production-projects')) return true;
      return isProjectDevelopmentModuleSubpath(path) && isProjectDetailFromProduction(currentUrl);
    }

    // Warranty: list/detail plus estimating/specs/selections opened with ?from=warranty
    if (route.endsWith('/warranty/projects')) {
      if (path.includes('/warranty/projects')) return true;
      return isProjectDevelopmentModuleSubpath(path) && isProjectDetailFromWarrantyProject(currentUrl);
    }

    // Company Settings → Files (`/admin/files`) vs Team → Files (`/team/files`, `/admin/team/files`)
    if (route.endsWith('/admin/files')) {
      return path === route || path.endsWith('/admin/files');
    }
    if (route.endsWith('/team/files') || route.endsWith('/admin/team/files')) {
      return path.includes('/team/files');
    }
    if (route.endsWith('/team/directory') || route.endsWith('/admin/team/directory')) {
      if (!path.includes('/team/directory')) return false;
      return !path.includes('/team/files') && !path.endsWith('/admin/files');
    }
    if (route.endsWith('/team/org-chart') || route.endsWith('/admin/team/org-chart')) {
      if (!path.includes('/team/org-chart')) return false;
      return !path.includes('/team/files') && !path.endsWith('/admin/files');
    }

    return currentUrl.includes(subItem.route);
  }

  // Handle parent item hover for tooltip
  onParentHover(event: MouseEvent, item: SidebarMenuItem) {
    if (!this._sidebarCollapsed || !item.children) return;

    // Clear any existing timeout when hovering over a new item
    if (this.tooltipHideTimeout) {
      clearTimeout(this.tooltipHideTimeout);
      this.tooltipHideTimeout = null;
    }

    this.hoveredItem = item;
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();

    // Position tooltip to align top with middle of the icon
    this.tooltipPosition = {
      left: rect.right + 8,
      top: rect.top + (rect.height / 2) - 16 // Center tooltip vertically with icon
    };

    // Create external tooltip component
    this.createExternalTooltip();
  }

  onParentLeave() {
    // If cursor moved into tooltip, keep it open
    if (this.hoveringTooltip) return;
    // Increased delay to prevent quick disappearances
    this.tooltipHideTimeout = setTimeout(() => {
      if (!this.hoveringTooltip) {
        this.hoveredItem = null;
        this.destroyExternalTooltip();
      }
    }, 300); // Increased from 180ms to 300ms
  }

  onTooltipEnter() {
    this.hoveringTooltip = true;
    // Clear any hide timeout when entering tooltip
    if (this.tooltipHideTimeout) {
      clearTimeout(this.tooltipHideTimeout);
      this.tooltipHideTimeout = null;
    }
  }

  onTooltipLeave() {
    this.hoveringTooltip = false;
    // Add a small delay before hiding to prevent flickering
    this.tooltipHideTimeout = setTimeout(() => {
      if (!this.hoveringTooltip) {
        this.hoveredItem = null;
        this.destroyExternalTooltip();
      }
    }, 200);
  }

  /**
   * Absolute URL for sidebar/tooltip anchors so native link behavior works:
   * right‑click → Open in new tab, Ctrl/Cmd+click → new tab, plain click → SPA (handled in click).
   */
  /** `target` for sidebar anchors (Sheets → new tab). */
  anchorTarget(item: SidebarMenuItem): string | null {
    if (this.showComingSoonChip(item) || item.disabled) {
      return null;
    }
    return item.externalTarget === '_blank' ? '_blank' : null;
  }

  /** `rel` when opening external links in a new tab. */
  anchorRel(item: SidebarMenuItem): string | null {
    return this.anchorTarget(item) === '_blank' ? 'noopener noreferrer' : null;
  }

  /** Show launch icon for items that open in a new tab (e.g. Sheets). */
  showsExternalLaunchIcon(item: SidebarMenuItem): boolean {
    return item.externalTarget === '_blank';
  }

  hrefForSidebarItem(item: SidebarMenuItem): string | null {
    if (this.showComingSoonChip(item) || item.disabled) {
      return null;
    }
    if (item.externalHref) {
      return item.externalHref;
    }
    if (item.route) {
      const path = item.route.startsWith('/') ? item.route : `/${item.route}`;
      return `${window.location.origin}${path}`;
    }
    return null;
  }

  /**
   * Same-tab in-app navigation on plain left click; leave default for modifiers / middle button
   * so the browser can open a new tab using `href`.
   */
  private handleSidebarAnchorClick(event: MouseEvent, item: SidebarMenuItem): boolean {
    if (this.showComingSoonChip(item)) {
      event.preventDefault();
      this.openComingSoonModal(item.label);
      return true;
    }
    if (item.disabled) {
      event.preventDefault();
      return false;
    }
    if (item.externalHref) {
      return true;
    }
    if (!item.route) {
      return false;
    }
    if (event.button !== 0) {
      return true;
    }
    if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) {
      return true;
    }
    event.preventDefault();
    const targetUrl = item.route.startsWith('/') ? item.route : `/${item.route}`;
    if (!this.navigationGuardService.attemptNavigation([targetUrl])) {
      return true;
    }
    void this.router.navigateByUrl(targetUrl);
    return true;
  }

  // Navigate to sub-item from tooltip (anchor click with full MouseEvent for modifier keys)
  navigateToSubItem(payload: { event: MouseEvent; subItem: SidebarMenuItem }): void {
    const { event, subItem } = payload;
    const closeTooltip = this.handleSidebarAnchorClick(event, subItem);
    if (closeTooltip) {
      this.hoveredItem = null;
      this.hoveringTooltip = false;
      this.destroyExternalTooltip();
    }
  }

  // Create external tooltip component
  private createExternalTooltip() {
    // Destroy existing tooltip if any
    this.destroyExternalTooltip();

    // Create new tooltip component
    this.tooltipComponentRef = createComponent(SidebarTooltipComponent, {
      environmentInjector: this.injector
    });

    // Set inputs
    this.tooltipComponentRef.setInput('hoveredItem', this.hoveredItem);
    this.tooltipComponentRef.setInput('position', this.tooltipPosition);
    this.tooltipComponentRef.setInput('isSubItemActive', this.isSubItemActive.bind(this));
    this.tooltipComponentRef.setInput('resolveHref', (item: SidebarMenuItem) => this.hrefForSidebarItem(item));

    // Set up event handlers
    this.tooltipComponentRef.instance.tooltipEnter.subscribe(() => {
      this.onTooltipEnter();
    });

    this.tooltipComponentRef.instance.tooltipLeave.subscribe(() => {
      this.onTooltipLeave();
    });

    this.tooltipComponentRef.instance.navigateToSubItem.subscribe((payload: { event: MouseEvent; subItem: SidebarMenuItem }) => {
      this.navigateToSubItem(payload);
    });

    // Attach to document body
    document.body.appendChild(this.tooltipComponentRef.location.nativeElement);
    this.appRef.attachView(this.tooltipComponentRef.hostView);
  }

  // Destroy external tooltip component
  private destroyExternalTooltip() {
    if (this.tooltipComponentRef) {
      this.appRef.detachView(this.tooltipComponentRef.hostView);
      this.tooltipComponentRef.destroy();
      this.tooltipComponentRef = null;
    }
  }

  // Handle direct navigation clicks (for both main items and sub-items)
  onSubmenuParentClick(event: Event, sub: SidebarMenuItem): void {
    event.stopPropagation();
    if (this.showComingSoonChip(sub)) {
      this.openComingSoonModal(sub.label);
    }
  }

  onDirectNavigationClick(event: MouseEvent, item: SidebarMenuItem): void {
    event.stopPropagation();
    this.handleSidebarAnchorClick(event, item);
  }

  private updateMenuRoutes() {
    const tenantId = this.tenantId();
    const basePath = tenantId ? `/${tenantId}` : '';
    const workbookBase = environment.storageWorkbookBaseUrl.replace(/\/$/, '');
    const storageWorkbookHref = tenantId ? `${workbookBase}/${tenantId}` : undefined;
    const wipBaseRaw = environment.wipBaseUrl ?? '';
    const wipBase = wipBaseRaw.replace(/\/$/, '');
    const accessToken = this.storageService.get<string>(STORAGE.ACCESS_TOKEN) ?? '';
    const wipExternalHref =
      wipBase && tenantId && accessToken
        ? buildWipHandoffUrl({ wipBaseUrl: wipBase, tenantId, accessToken })
        : undefined;
    const isAdminMode = this.isAdminMode();
    const currentUrl = this.router.url;
    const isAdminDirect = currentUrl.includes('/admin');

    // Use direct URL check as fallback
    const shouldUseAdminMode = isAdminMode || isAdminDirect;

    // Load expanded states from storage
    const storedExpandedStates = this.storageService.get<{ [key: string]: boolean }>(STORAGE.SIDEBAR_EXPANDED_STATES) || {};

    // Store current expanded states
    const expandedStates = new Map<string, boolean>();
    if (this.menu.length > 0) {
      this.menu.forEach(item => {
        if (item.children) {
          expandedStates.set(item.label, item.expanded || false);
        }
      });
    }

    const mainTeamChildren = this.teamSidebarChildren(basePath);

    if (shouldUseAdminMode) {
      // Admin mode menu
      this.menu = [
        {
          label: 'Company Settings',
          icon: 'assets/icons/company-settings.svg',
          iconActive: 'assets/icons/company-settings.svg',
          chevron: true,
          expanded:
            storedExpandedStates['Company Settings'] ?? expandedStates.get('Company Settings') ?? false,
          children: this.adminCompanySettingsChildren(basePath),
        },
        {
          label: 'Estimating Settings',
          icon: 'assets/icons/estimation-settings.svg',
          iconActive: 'assets/icons/estimation-settings.svg',
          chevron: true,
          expanded:
            storedExpandedStates['Estimating Settings'] ?? expandedStates.get('Estimating Settings') ?? false,
          children: [
            { label: 'Cost Code Library', route: `${basePath}/admin/cost-code-library` },
            { label: 'Financial Layering & Markups', route: `${basePath}/admin/markups-and-fees` },
          ],
        },
        {
          label: 'Approval Rules',
          icon: 'assets/icons/sidebar_icons/badge-check.svg',
          iconActive: 'assets/icons/sidebar_icons/badge-check.svg',
          chevron: true,
          expanded:
            storedExpandedStates['Approval Rules'] ?? expandedStates.get('Approval Rules') ?? false,
          children: [
            { label: 'Estimating Tool', route: `${basePath}/admin/approval-rules/estimating` },
            { label: 'Change Order Tool', route: `${basePath}/admin/approval-rules/change-order` },
            { label: 'Cost Inbox', route: `${basePath}/admin/approval-rules/cost-inbox` },
          ],
        },
        {
          label: 'Labor Costs',
          icon: 'assets/icons/dollar-icon.svg',
          iconActive: 'assets/icons/dollar-icon.svg',
          chevron: true,
          expanded:
            storedExpandedStates['Labor Costs'] ?? expandedStates.get('Labor Costs') ?? false,
          children: [
            {
              label: 'Global Labor Settings',
              route: `${basePath}/admin/global-labor-costing`,
              disabled: !this.hasLaborSettingsViewPermission(),
            },
            {
              label: 'Employee Labor Rates',
              route: `${basePath}/admin/labor-rates`,
              disabled: !this.hasLaborCostViewPermission(),
            },
          ],
        },
        {
          label: 'Finance Settings',
          icon: 'assets/icons/dollar-icon.svg',
          iconActive: 'assets/icons/dollar-icon.svg',
          chevron: true,
          expanded:
            storedExpandedStates['Finance Settings'] ?? expandedStates.get('Finance Settings') ?? false,
          children: [
            {
              label: 'QBO Account',
              route: `${basePath}/admin/finance-settings/qbo-account`,
              disabled: !this.hasAccountingSyncPermission(),
              disabledTooltip: 'Requires an Accounting sync permission',
            },
          ],
        },
        {
          label: 'Time Approval',
          icon: 'assets/icons/clock.svg',
          iconActive: 'assets/icons/clock.svg',
          route: `${basePath}/admin/time-approval`,
          disabled: false,
        },
        {
          label: 'Data Management',
          icon: 'assets/icons/data-manage-settings.svg',
          iconActive: 'assets/icons/data-manage-settings.svg',
          route: `${basePath}/admin/data-management`,
        },
        {
          label: 'Integrations',
          icon: 'assets/icons/integration-settings.svg',
          iconActive: 'assets/icons/integration-settings.svg',
          route: `${basePath}/admin/integrations`,
        },
        {
          label: 'Users & Permissions',
          icon: 'assets/icons/user-perms-settings.svg',
          iconActive: 'assets/icons/user-perms-settings.svg',
          chevron: true,
          expanded:
            storedExpandedStates['Users & Permissions'] ?? expandedStates.get('Users & Permissions') ?? false,
          children: [
            {
              label: 'Users',
              route: `${basePath}/admin/users`,
              disabled: !this.hasUsersPermission(),
            },
            {
              label: 'Roles',
              route: `${basePath}/admin/roles`,
              disabled: !this.hasRolesPermission(),
            },
          ],
        },
      ];
    } else {
      // Regular mode menu – structure and order match new design
      this.menu = [
        {
          label: 'Sales',
          icon: 'assets/icons/Sales.svg',
          iconActive: 'assets/icons/Sales.svg',
          chevron: true,
          expanded: storedExpandedStates['Sales'] ?? expandedStates.get('Sales') ?? false,
          children: [
            { label: 'Leads', icon: 'assets/icons/sidebar_icons/leads.svg', iconActive: 'assets/icons/sidebar_icons/leads--white.svg', route: `${basePath}/leads`, disabled: !this.hasLeadsPermission() },
            { label: 'Opportunities', icon: 'assets/icons/sidebar_icons/oppertunity.svg', iconActive: 'assets/icons/sidebar_icons/oppertunity--white.svg', route: `${basePath}/opportunities`, disabled: !this.hasOpportunitiesPermission() },
            { label: 'Insights', icon: 'assets/icons/sidebar_icons/insights.svg', iconActive: 'assets/icons/sidebar_icons/insights--white.svg', route: `${basePath}/insights`, disabled: false },
          ]
        },
        {
          label: 'Project Development',
          icon: 'assets/icons/project-development.svg',
          iconActive: 'assets/icons/project-development.svg',
          chevron: true,
          expanded: storedExpandedStates['Project Development'] ?? expandedStates.get('Project Development') ?? false,
          children: [
            { label: 'Contract Development', icon: 'assets/icons/sidebar_icons/task-list.svg', route: `${basePath}/projects/contract-development` },
            { label: 'Projects', icon: 'assets/icons/sidebar_icons/clipboard-check.svg', route: `${basePath}/projects` },
            { label: 'Estimate Templates', icon: 'assets/icons/sidebar_icons/bookmark-book.svg', route: `${basePath}/estimating`, disabled: false },
          ]
        },
        {
          label: 'Production',
          icon: 'assets/icons/Production.svg',
          iconActive: 'assets/icons/Production.svg',
          chevron: true,
          expanded: storedExpandedStates['Production'] ?? expandedStates.get('Production') ?? false,
          children: [
            { label: 'Projects', icon: 'assets/icons/sidebar_icons/clipboard-check.svg', route: `${basePath}/production-projects` },
            {
              label: 'WIP',
              icon: 'assets/icons/sidebar_icons/finance.svg',
              externalHref: wipExternalHref,
              externalTarget: '_blank',
              disabled: !wipExternalHref,
              disabledTooltip: !tenantId
                ? 'Select a company workspace to open WIP'
                : !accessToken
                  ? 'Sign in to open WIP'
                  : !wipBase
                    ? 'WIP is not configured for this environment'
                    : undefined,
            },
          ]
        },
        {
          label: 'Finance',
          icon: 'assets/icons/sidebar_icons/finance.svg',
          iconActive: 'assets/icons/sidebar_icons/finance.svg',
          chevron: true,
          expanded:
            storedExpandedStates['Finance'] ??
            expandedStates.get('Finance') ??
            (currentUrl.includes('/finance/cost-inbox') || currentUrl.includes('/finance/job-costing')),
          children: [
            {
              label: 'Cost Inbox',
              icon: 'assets/icons/sidebar_icons/inbox.svg',
              iconActive: 'assets/icons/sidebar_icons/inbox--white.svg',
              route: `${basePath}/finance/cost-inbox`,
            },
            {
              label: 'Job Costing',
              icon: 'assets/icons/sidebar_icons/wallet.svg',
              route: `${basePath}/finance/job-costing`,
              disabled: !this.hasJobCostingPermission(),
            },
          ],
        },
        {
          label: 'Communications',
          icon: 'assets/icons/sidebar_icons/message-text.svg',
          iconActive: 'assets/icons/sidebar_icons/message-text.svg',
          chevron: true,
          expanded:
            storedExpandedStates['Communications'] ??
            storedExpandedStates['Messages'] ??
            expandedStates.get('Communications') ??
            expandedStates.get('Messages') ??
            false,
          children: [
            { label: 'Messages', icon: 'assets/icons/sidebar_icons/message-text.svg', route: `${basePath}/my-feed` },
            { label: 'Inbox', icon: 'assets/icons/sidebar_icons/inbox.svg', iconActive: 'assets/icons/sidebar_icons/inbox--white.svg', route: `${basePath}/inbox` },
          ]
        },
        {
          label: 'Business Infrastructure',
          icon: 'assets/icons/business-infrastructure.svg',
          iconActive: 'assets/icons/business-infrastructure.svg',
          chevron: true,
          expanded:
            storedExpandedStates['Business Infrastructure'] ??
            expandedStates.get('Business Infrastructure') ??
            false,
          children: [
            {
              label: 'Contacts',
              icon: 'assets/icons/contacts.svg',
              iconActive: 'assets/icons/contacts.svg',
              route: `${basePath}/contacts`,
              disabled: !this.hasContactsPermission(),
            },
            {
              label: 'Asset Management',
              icon: 'assets/icons/sidebar_icons/multiple-pages.svg',
              route: `${basePath}/asset-management`,
              disabled: true,
              comingSoon: true,
            },
            ...(mainTeamChildren.length > 0
              ? [
                  {
                    label: 'Team',
                    icon: 'assets/icons/teams-icon.svg',
                    iconActive: 'assets/icons/teams-icon.svg',
                    chevron: true,
                    expanded:
                      storedExpandedStates['Team'] ?? expandedStates.get('Team') ?? false,
                    children: mainTeamChildren,
                  },
                ]
              : []),
          ],
        },
        {
          label: 'Tools & Utilities',
          icon: 'assets/icons/sidebar_icons/tools.svg',
          iconActive: 'assets/icons/sidebar_icons/tools.svg',
          chevron: true,
          expanded:
            storedExpandedStates['Tools & Utilities'] ??
            storedExpandedStates['Tools'] ??
            expandedStates.get('Tools & Utilities') ??
            expandedStates.get('Tools') ??
            false,
          children: [
            { label: 'Workboards', icon: 'assets/icons/sidebar_icons/rule.svg', route: `${basePath}/task-management`, disabled: !this.hasWorkboardsPermission() },
            {
              label: 'Sheets',
              icon: 'assets/icons/sidebar_icons/journal-page.svg',
              externalHref: storageWorkbookHref,
              externalTarget: '_blank',
              disabled: !tenantId,
              disabledTooltip: 'Select a company workspace to open Sheets',
            },
            { label: 'Calendar', icon: 'assets/icons/calendar.svg', route: `${basePath}/calendar` },
            {
              label: 'Time Clock',
              icon: 'assets/icons/clock.svg',
              route: `${basePath}/time-clock`,
            },
            {
              label: 'Pulse',
              icon: 'assets/icons/financial-goals.svg',
              route: `${basePath}/targets`,
              disabled: true,
              comingSoon: true,
            },
          ]
        },
        {
          label: 'Warranty',
          icon: 'assets/icons/sidebar_icons/verified_user.svg',
          iconActive: 'assets/icons/sidebar_icons/verified_user.svg',
          route: `${basePath}/warranty/projects`,
        },
        {
          label: 'AI Chat',
          icon: 'assets/icons/sidebar_icons/message-text.svg',
          iconActive: 'assets/icons/sidebar_icons/message-text.svg',
          route: `${basePath}/ai-chat`,
        },
      ];
    }
  }

  toggleExpand(event: Event, item: SidebarMenuItem) {
    event.stopPropagation();
    if (this._sidebarCollapsed) {
      this.expandSidebar.emit();
      // When expanding from collapsed state, don't toggle the expanded state
      // Just ensure the item is expanded if it was already expanded
      setTimeout(() => {
        // If the item was already expanded, keep it expanded
        // If it wasn't expanded, expand it
        if (!item.expanded) {
          item.expanded = true;
          this.saveExpandedStates();
        }
        // Check for scrollable content after expansion
        this.checkScrollableContent();
      }, 200);
    } else {
      item.expanded = !item.expanded;
      this.saveExpandedStates();
      // Check for scrollable content after toggle
      setTimeout(() => {
        this.checkScrollableContent();
      }, 100);
    }
  }

  private saveExpandedStates(): void {
    const expandedStates: { [key: string]: boolean } = {};

    this.menu.forEach(item => {
      if (item.children) {
        expandedStates[item.label] = item.expanded || false;
      }
    });

    this.storageService.set(STORAGE.SIDEBAR_EXPANDED_STATES, expandedStates);
  }

  ngOnDestroy() {
    this.routeEventsSub?.unsubscribe();
    this.permissionsChangedSub?.unsubscribe();
    // Clean up external tooltip when component is destroyed
    this.destroyExternalTooltip();

    // Clear any pending timeouts
    if (this.tooltipHideTimeout) {
      clearTimeout(this.tooltipHideTimeout);
    }
  }
}
